import { useState, useCallback, useRef } from 'react';
import { sendChatMessage } from '../api/chat';
import { startScrapeJob, getScrapeStatus, listProfiles } from '../api/scrape';
import { searchProspects, listProspects } from '../api/prospects';
import { emit } from '../api/events';

const welcomeMessage = {
  id: 1,
  sender: 'archer',
  text: "Welcome to Archer! I'm your AI outbound assistant. I'll help you build your voice profile, define your ideal customer, and craft messaging that sounds like you.\n\nTip:\n- Paste a **website URL** to analyze a company\n- Say **\"search prospects\"** to find leads from your ICP (e.g. \"search 200 prospects\")",
  timestamp: new Date(),
};

function detectProspectSearch(text) {
  const lower = text.toLowerCase();
  const patterns = [
    /search\s+(?:for\s+)?(\d+)?\s*prospects?/i,
    /find\s+(?:me\s+)?(\d+)?\s*prospects?/i,
    /搜索\s*(\d+)?\s*潜客/,
    /找\s*(\d+)?\s*(?:个\s*)?潜客/,
    /prospect\s*search\s*(\d+)?/i,
    /search\s+(\d+)\s+(?:leads?|contacts?|people)/i,
  ];
  for (const pat of patterns) {
    const m = text.match(pat);
    if (m) {
      const count = m[1] ? Math.min(1000, Math.max(10, parseInt(m[1], 10))) : 500;
      return { match: true, count };
    }
  }
  if (/(?:search|find)\s+(?:leads?|contacts?)/i.test(lower) || /搜[潜线]索?客/.test(lower)) {
    return { match: true, count: 500 };
  }
  return null;
}

function extractUrl(text) {
  const urlMatch = text.match(/https?:\/\/[^\s]+/i);
  if (urlMatch) return urlMatch[0];
  const domainMatch = text.match(/(?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?/i);
  if (domainMatch && text.trim().split(/\s+/).length <= 3) return `https://${domainMatch[0]}`;
  return null;
}

function formatProfile(profile) {
  let lines = [];
  if (profile.companyName) lines.push(`**${profile.companyName}**`);
  if (profile.description) lines.push(profile.description);
  if (profile.industry) lines.push(`Industry: ${profile.industry}`);
  if (profile.targetMarket) lines.push(`Target: ${typeof profile.targetMarket === 'string' ? profile.targetMarket : JSON.stringify(profile.targetMarket)}`);
  if (profile.services?.length) {
    lines.push('\nServices:');
    profile.services.slice(0, 6).forEach(s => lines.push(`  - ${s}`));
    if (profile.services.length > 6) lines.push(`  - ...and ${profile.services.length - 6} more`);
  }
  if (profile.talkingPoints?.length) {
    lines.push('\nTalking Points:');
    profile.talkingPoints.forEach(t => lines.push(`  - ${t}`));
  }
  lines.push('\nFull profile saved — view it in Voice Profile & ICP page.');
  return lines.join('\n');
}

export default function useChatMessages() {
  const [messages, setMessages] = useState([welcomeMessage]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const conversationIdRef = useRef(null);

  const addMessage = useCallback((msg) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), timestamp: new Date(), ...msg }]);
  }, []);

  const pollForResult = useCallback(async (jobId) => {
    for (let i = 0; i < 40; i++) { // poll up to 2 minutes
      await new Promise(r => setTimeout(r, 3000));
      try {
        const data = await getScrapeStatus(jobId);
        if (data.status === 'completed' && data.profile) {
          return data.profile;
        }
        if (data.status === 'failed') {
          throw new Error(data.error || 'Analysis failed');
        }
      } catch (err) {
        if (err.message !== 'Analysis failed') continue;
        throw err;
      }
    }
    throw new Error('Analysis timed out. Check Voice Profile page for results.');
  }, []);

  const pollProspects = useCallback(async (targetCount) => {
    let stableCount = 0;
    let lastTotal = -1;
    for (let i = 0; i < 100; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const data = await listProspects();
        const newTotal = data.total || 0;
        if (newTotal === lastTotal) stableCount++;
        else stableCount = 0;
        lastTotal = newTotal;

        // Update the "Searching..." message with progress
        setMessages(prev => {
          const idx = prev.findLastIndex(m => m.isSearching);
          if (idx === -1) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], text: `Searching Apollo for prospects... **${newTotal}** found so far.` };
          return updated;
        });

        if ((stableCount >= 3 && newTotal > 0) || i >= 99) {
          return newTotal;
        }
      } catch { /* continue polling */ }
    }
    return lastTotal;
  }, []);

  const sendMessage = useCallback(async (text) => {
    addMessage({ sender: 'user', text });
    setLoading(true);
    setError(null);

    const prospectCmd = detectProspectSearch(text);
    const url = !prospectCmd ? extractUrl(text) : null;

    try {
      if (prospectCmd) {
        // Load voice profiles to get the latest
        const profilesData = await listProfiles();
        const profiles = profilesData.profiles || [];
        if (profiles.length === 0) {
          addMessage({ sender: 'archer', text: 'No voice profile found. Please analyze your company URL first (paste a URL in chat or go to Voice Profile & ICP page).' });
          return;
        }
        const latest = profiles[0];
        addMessage({
          sender: 'archer',
          text: `Searching Apollo for prospects using ICP from **${latest.companyName}** (target: ${prospectCmd.count})...`,
          isSearching: true,
        });

        await searchProspects({ voiceProfileJobId: latest.jobId, targetCount: prospectCmd.count });
        const finalTotal = await pollProspects(prospectCmd.count);

        setMessages(prev => {
          const idx = prev.findLastIndex(m => m.isSearching);
          if (idx === -1) return prev;
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            text: `Done! Found **${finalTotal}** prospects from your ICP. Go to the **Prospects** page to view, generate briefs, and manage them.`,
            isSearching: false,
          };
          return updated;
        });
        emit('prospects-updated');
      } else if (url) {
        addMessage({ sender: 'archer', text: `Analyzing ${url}... This may take up to a minute.` });

        const data = await startScrapeJob(url);

        if (data.status === 'completed' && data.profile) {
          setMessages(prev => [...prev.slice(0, -1), {
            id: Date.now(), sender: 'archer', text: formatProfile(data.profile),
            timestamp: new Date(), isProfile: true,
          }]);
          emit('profile-updated');
        } else {
          // Async — poll for result
          const profile = await pollForResult(data.jobId);
          setMessages(prev => [...prev.slice(0, -1), {
            id: Date.now(), sender: 'archer', text: formatProfile(profile),
            timestamp: new Date(), isProfile: true,
          }]);
          emit('profile-updated');
        }
      } else {
        const data = await sendChatMessage(text, conversationIdRef.current);
        conversationIdRef.current = data.conversationId || conversationIdRef.current;
        addMessage({ sender: 'archer', text: data.reply || data.message || 'No response received.' });
      }
    } catch (err) {
      let errorText;
      if (err.status === 401) errorText = 'Session expired. Please refresh and log in again.';
      else if (err.status === 502) errorText = 'AI service temporarily unavailable. Try again later.';
      else if (err.message?.includes('Failed to fetch')) errorText = 'Network error. Check your connection.';
      else errorText = err.message || 'Something went wrong. Please try again.';

      // If there's an "Analyzing..." or "Searching..." message, replace it
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.text?.startsWith('Analyzing') || last?.isSearching) {
          return [...prev.slice(0, -1), { id: Date.now(), sender: 'archer', text: errorText, timestamp: new Date(), isError: true }];
        }
        return [...prev, { id: Date.now(), sender: 'archer', text: errorText, timestamp: new Date(), isError: true }];
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [addMessage, pollForResult, pollProspects]);

  return { messages, sendMessage, loading, error };
}
